<div class="report-container">
  <div class="report-header">
    <div class="title-section">
      <h2 class="modern-title">Statement of Cash Flows</h2>
      <p class="modern-subtitle">Tracking money moving in and out of your business</p>
    </div>

    <div class="controls-section">
      <div class="btn-group modern-btn-group">
        @for (p of periods; track p) {
          <button
            class="btn modern-filter-btn"
            [class.active]="period === p"
            (click)="setPeriod(p)">
            {{ p }}
          </button>
        }
      </div>

      @if (period === 'Custom') {
        <div class="custom-date-section ms-3 d-flex align-items-center">
          <input type="date" [(ngModel)]="startDate" class="date-input">
          <span class="mx-2" style="color: white">to</span>
          <input type="date" [(ngModel)]="endDate" class="date-input">
          <button class="btn modern-action-btn primary-btn ms-2" (click)="applyCustomRange()">Apply</button>
        </div>
      }

      <button class="btn modern-action-btn primary-btn ms-3" (click)="exportPdf()">
        Export PDF
      </button>
    </div>
  </div>

  <!-- Summary Cards -->
  <div class="summary-cards">
    <div class="summary-card glass-card">
      <div class="card-details">
        <div class="card-period">{{ period }}</div>
        <div class="card-label">Operating Cash</div>
        <div class="card-value text-operating">{{ netOperating | currency }}</div>
      </div>
    </div>
    <div class="summary-card glass-card">
      <div class="card-details">
        <div class="card-period">{{ period }}</div>
        <div class="card-label">Investing Cash</div>
        <div class="card-value text-investing">{{ netInvesting | currency }}</div>
      </div>
    </div>
    <div class="summary-card glass-card">
      <div class="card-details">
        <div class="card-period">{{ period }}</div>
        <div class="card-label">Financing Cash</div>
        <div class="card-value text-financing">{{ netFinancing | currency }}</div>
      </div>
    </div>
    <div class="summary-card glass-card highlight-card">
      <div class="card-details">
        <div class="card-period">{{ period }}</div>
        <div class="card-label">Net Change in Cash</div>
        <div class="card-value" [class.text-success]="netChangeInCash >= 0" [class.text-danger]="netChangeInCash < 0">
          {{ netChangeInCash > 0 ? '+' : '' }}{{ netChangeInCash | currency }}
        </div>
      </div>
    </div>
  </div>

  <!-- Chart & Ending Cash -->
  <div class="row mt-4">
    <div class="col-md-9">
      <div class="chart-section glass-card h-100">
        <div class="section-header d-flex justify-content-between align-items-center mb-3">
          <div class="d-flex align-items-center">
            <h3 class="section-title text-white mb-0">Cash Flow Breakdown</h3>
            <span class="ms-2 period-tag">{{ period }}</span>
          </div>
          <button class="btn btn-link text-white p-0" type="button" aria-label="Toggle cash flow chart" title="Toggle cash flow chart" (click)="isChartCollapsed = !isChartCollapsed"></button>
        </div>
        <div [collapse]="isChartCollapsed" [isAnimated]="true">
          <div class="chart-wrapper">
            <div class="row h-100 g-0">
              <div class="col-md-8 border-end border-white border-opacity-10 d-flex justify-content-center">
                @if (chartOptions) {
                  <ag-charts [options]="chartOptions" style="height: 100%; width: 100%;"></ag-charts>
                }
              </div>
              <div class="col-md-4 d-flex justify-content-center">
                @if (compositionOptions) {
                  <ag-charts [options]="compositionOptions" style="height: 100%; width: 100%;"></ag-charts>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="col-md-3 mt-4 mt-md-0">
      <div class="glass-card h-100 d-flex flex-column justify-content-center align-items-center text-center p-4">
        <h4 class="text-light mb-2 text-uppercase fw-medium" style="letter-spacing: 0.1em; font-size: 13px;">Beginning Cash Balance</h4>
        <h2 class="text-white mb-4">{{ beginningCash | currency }}</h2>

        <h4 class="text-light mb-2 text-uppercase fw-medium" style="letter-spacing: 0.1em; font-size: 13px;">Ending Cash Balance</h4>
        <h1 class="text-success fw-medium mb-0" style="font-size: 3rem;">{{ endingCash | currency }}</h1>
      </div>
    </div>
  </div>

  <!-- Detailed Breakdown Tables -->
  <div class="breakdown-card glass-card mt-4 mb-4">
    <div class="section-header d-flex justify-content-between align-items-center mb-3"
      (click)="isBreakdownCollapsed = !isBreakdownCollapsed"
      style="cursor: pointer">
      <div class="d-flex align-items-center">
        <h3 class="section-title text-white mb-0">Detailed Activity Breakdown</h3>
        <span class="ms-2 period-tag">{{ period }}</span>
      </div>
      <button class="btn btn-link text-white p-0" type="button" aria-label="Toggle detailed activity breakdown" title="Toggle detailed activity breakdown"></button>
    </div>

    <div [collapse]="isBreakdownCollapsed" [isAnimated]="true">
      <!-- Operating -->
      <div class="section-header d-flex justify-content-between align-items-center mb-3">
        <h3 class="section-title text-operating mb-0">Operating Activities</h3>
        <span class="badge operating-badge">{{ netOperating | currency }}</span>
      </div>
      <div class="table-responsive mb-5">
        <table class="table modern-table table-hover">
          <tbody>
            @for (row of operatingActivities; track row) {
              <tr>
                <td style="color: white">{{ row.name }}</td>
                <td class="text-end fw-medium" [class.text-success]="row.amount > 0" [class.text-danger]="row.amount < 0" [class.text-white]="row.amount === 0">
                  {{ row.amount | currency }}
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <!-- Investing -->
      <div class="section-header d-flex justify-content-between align-items-center mb-3">
        <h3 class="section-title text-investing mb-0">Investing Activities</h3>
        <span class="badge investing-badge">{{ netInvesting | currency }}</span>
      </div>
      <div class="table-responsive mb-5">
        <table class="table modern-table table-hover">
          <tbody>
            @for (row of investingActivities; track row) {
              <tr>
                <td style="color: white">{{ row.name }}</td>
                <td class="text-end fw-medium" [class.text-success]="row.amount > 0" [class.text-danger]="row.amount < 0" [class.text-white]="row.amount === 0">
                  {{ row.amount | currency }}
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <!-- Financing -->
      <div class="section-header d-flex justify-content-between align-items-center mb-3">
        <h3 class="section-title text-financing mb-0">Financing Activities</h3>
        <span class="badge financing-badge">{{ netFinancing | currency }}</span>
      </div>
      <div class="table-responsive">
        <table class="table modern-table table-hover">
          <tbody>
            @for (row of financingActivities; track row) {
              <tr>
                <td style="color: white">{{ row.name }}</td>
                <td class="text-end fw-medium" [class.text-success]="row.amount > 0" [class.text-danger]="row.amount < 0" [class.text-white]="row.amount === 0">
                  {{ row.amount | currency }}
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  </div>
</div>
